/**
 * Canonical role mapping for the beta invite → membership → passport
 * pipeline.
 *
 * Two role spaces co-exist in OptiBay and MUST NOT be cross-pollinated
 * by direct string copy:
 *
 *   - **Membership roles** are uppercase permission strings on
 *     `memberships.role` (e.g. `OWNER`, `MANAGER`, `SERVICE_WRITER`,
 *     `TECHNICIAN`, `ORG_ADMIN`). They drive RLS and edge-function
 *     authorization. The full enum lives in
 *     `supabase/functions/_shared/authGuard.ts` (ValidateAccessOptions
 *     `requireRoles`).
 *
 *   - **Passport roles** are lowercase professional identity strings on
 *     `passport_profiles.roles[]` (e.g. `owner`, `org_admin`, `manager`,
 *     `advisor`, `technician`, `student`, `instructor`, `school_admin`).
 *     They drive Passport surface rendering and workforce/discovery
 *     filtering. The CHECK constraint lives in the
 *     `widen_passport_roles_with_leadership` migration that ships with
 *     this batch.
 *
 * Lead role strings (`beta_leads.role` / `beta_invites.role_requested`)
 * come from a free-text admin form and need normalization before they
 * can drive either downstream space. This module is the single
 * authority for that normalization.
 *
 * Use the named helpers — do NOT inline the mapping at call sites.
 * Adding a new lead-role string only requires editing this file +
 * its tests.
 */

// ─── Membership roles (uppercase) ──────────────────────────────────────
//
// Mirror of the authoritative `memberships.role` enum used by RLS.
export type MembershipRole =
  | 'OWNER'
  | 'ORG_ADMIN'
  | 'MANAGER'
  | 'SERVICE_WRITER'
  | 'TECHNICIAN'
  | 'DISPATCHER'
  | 'PARTS'
  | 'AUDITOR';

// ─── Passport roles (lowercase) ────────────────────────────────────────
//
// Mirror of `passport_profiles.roles[]` after the leadership widening
// migration. Keep in sync with ROLE_OPTIONS in PassportHome.tsx.
export type PassportRole =
  | 'owner'
  | 'org_admin'
  | 'manager'
  | 'advisor'
  | 'technician'
  | 'student'
  | 'instructor'
  | 'school_admin';

export const ALL_PASSPORT_ROLES: readonly PassportRole[] = [
  'owner',
  'org_admin',
  'manager',
  'advisor',
  'technician',
  'student',
  'instructor',
  'school_admin',
] as const;

// ─── Normalization helpers ─────────────────────────────────────────────

/**
 * Normalize free-text lead-role strings into a stable key. The admin
 * form, the public beta application, and historical seed data have all
 * produced slightly different casings ("Owner", "OWNER", "service
 * writer", "Service Writer", "Advisor / Service Writer"). We collapse
 * those before any mapping decision.
 */
function normalizeLeadRoleKey(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw
    .trim()
    .toLowerCase()
    // Treat "Service Writer / Advisor" and "Advisor / Service Writer" as one bucket.
    .replace(/[/]/g, ' ')
    .replace(/\s+/g, ' ');
}

// ─── Lead role → membership role ───────────────────────────────────────

/**
 * Map a lead-role string to a canonical membership role.
 *
 * SUP-13 (K86): returns `null` for any input that is missing, empty, or
 * unrecognized. There is NO `MANAGER` fallback floor — the historical
 * `?? 'MANAGER'` default silently granted an unknown/typo'd/school invite
 * full shop-manager access, a privilege over-grant. A `null` return means
 * "this role does not map to a shop membership; the caller MUST reject,
 * never floor." `provision-beta-org` guards with isSchoolLeadRole /
 * isRecognizedBetaLeadRole first AND treats a null map as a hard 4xx.
 *
 * Special case — `student`, `instructor`, `school_admin` also return
 * `null` (school identities with no shop permission grant). They are
 * indistinguishable here from any other unprovisionable role: all of them
 * mean "do not create a shop membership."
 */
export function betaLeadRoleToMembershipRole(
  raw: string | null | undefined,
): MembershipRole | null {
  const key = normalizeLeadRoleKey(raw);
  switch (key) {
    case 'owner':
      return 'OWNER';
    case 'org admin':
    case 'org_admin':
    case 'organization admin':
      return 'ORG_ADMIN';
    case 'manager':
    case 'general manager':
    case 'shop manager':
      return 'MANAGER';
    case 'service writer':
    case 'service writer advisor':
    case 'advisor service writer':
    case 'advisor':
      return 'SERVICE_WRITER';
    case 'technician':
    case 'tech':
      return 'TECHNICIAN';
    case 'dispatcher':
      return 'DISPATCHER';
    case 'parts':
    case 'parts manager':
      return 'PARTS';
    case 'auditor':
      return 'AUDITOR';
    // School-side identities don't get shop memberships.
    case 'student':
    case 'instructor':
    case 'school admin':
    case 'school_admin':
      return null;
    // SUP-13: empty / unrecognized → null (NO MANAGER floor). The caller
    // must reject rather than silently elevate an unknown role.
    default:
      return null;
  }
}

// SUP-13 (K38): mirror of canonicalBetaRoles.ts — keys that map to a REAL
// shop role (explicit, non-default branches) vs school-side identities. Lets
// callers reject unknown/ambiguous roles instead of silently defaulting to
// MANAGER.
const RECOGNIZED_SHOP_LEAD_ROLE_KEYS: ReadonlySet<string> = new Set([
  'owner',
  'org admin', 'org_admin', 'organization admin',
  'manager', 'general manager', 'shop manager',
  'service writer', 'service writer advisor', 'advisor service writer', 'advisor',
  'technician', 'tech',
  'dispatcher',
  'parts', 'parts manager',
  'auditor',
]);

const SCHOOL_LEAD_ROLE_KEYS: ReadonlySet<string> = new Set([
  'student', 'instructor', 'school admin', 'school_admin',
]);

/** True only for lead-role strings that explicitly map to a shop role. */
export function isRecognizedBetaLeadRole(raw: string | null | undefined): boolean {
  return RECOGNIZED_SHOP_LEAD_ROLE_KEYS.has(normalizeLeadRoleKey(raw));
}

/** True for school-side identities (student/instructor/school_admin). */
export function isSchoolLeadRole(raw: string | null | undefined): boolean {
  return SCHOOL_LEAD_ROLE_KEYS.has(normalizeLeadRoleKey(raw));
}

// ─── Lead role → passport role ─────────────────────────────────────────

/**
 * Map a lead-role string to the canonical passport-profile role.
 *
 * Defaults to `manager` to mirror the historical membership fallback
 * — keeps the two spaces aligned even when input is missing.
 *
 * Unlike the membership mapper, this never returns `null`: every lead
 * role has a passport-role equivalent (school identities have their own
 * passport roles).
 */
export function betaLeadRoleToPassportRole(
  raw: string | null | undefined,
): PassportRole {
  const key = normalizeLeadRoleKey(raw);
  switch (key) {
    case 'owner':
      return 'owner';
    case 'org admin':
    case 'org_admin':
    case 'organization admin':
      return 'org_admin';
    case 'manager':
    case 'general manager':
    case 'shop manager':
      return 'manager';
    case 'service writer':
    case 'service writer advisor':
    case 'advisor service writer':
    case 'advisor':
      return 'advisor';
    case 'technician':
    case 'tech':
      return 'technician';
    case 'student':
      return 'student';
    case 'instructor':
      return 'instructor';
    case 'school admin':
    case 'school_admin':
      return 'school_admin';
    default:
      return 'manager';
  }
}

// ─── Membership role → passport role ───────────────────────────────────

/**
 * Map an active membership role to the passport role we'd lock at
 * Passport creation. Used when the authoritative signal is the
 * server-verified membership (preferred over localStorage echo of the
 * invite).
 */
export function membershipRoleToPassportRole(
  role: MembershipRole | string | null | undefined,
): PassportRole | null {
  if (!role) return null;
  const upper = String(role).toUpperCase();
  switch (upper) {
    case 'OWNER':
      return 'owner';
    case 'ORG_ADMIN':
      return 'org_admin';
    case 'MANAGER':
      return 'manager';
    case 'SERVICE_WRITER':
      return 'advisor';
    case 'TECHNICIAN':
      return 'technician';
    // DISPATCHER, PARTS, AUDITOR have no Passport-side professional
    // identity bucket today. Return null so the caller falls through
    // to free selection.
    default:
      return null;
  }
}
